import { useEffect, useMemo, useState } from 'react';
import { PlusIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import type { Prayer, PrayerType, PrivacyLevel } from '@flock-types/prayer.types';
import { useAuth } from '@/context/AuthContext';
import { usePrayerUnreadContext } from '@/context/PrayerUnreadContext';
import { usePrayers } from '@/hooks/usePrayers';
import { usePrayerActions } from '@/hooks/usePrayerActions';

import PrayerCard from './PrayerCard';
import PrayerSearchPopover from './search/PrayerSearchPopover';

const STATUS_TABS = [
  { value: 'active', label: 'Active' },
  { value: 'answered', label: 'Answered' },
  { value: 'all', label: 'All' },
] as const;

const TYPE_TABS = [
  { value: 'all', label: 'All' },
  { value: 'prayer', label: 'Prayers' },
  { value: 'praise', label: 'Praises' },
] as const;

function CreatePrayerForm({
  onSubmit,
  onClose,
}: {
  onSubmit: (data: { prayerText: string; type: PrayerType; privacyLevel: PrivacyLevel }) => Promise<void>;
  onClose: () => void;
}) {
  const [text, setText] = useState('');
  const [type, setType] = useState<PrayerType>('prayer');
  const [privacy, setPrivacy] = useState<PrivacyLevel>('public');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="rounded-2xl border border-flock-navy-200 bg-white p-4 shadow-md">
      {error && (
        <div className="mb-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}
      <div className="mb-2 flex gap-2">
        {(['prayer', 'praise'] as PrayerType[]).map((t) => (
          <button
            key={t}
            onClick={() => setType(t)}
            className={clsx(
              'rounded-full px-3 py-1.5 text-xs font-medium capitalize transition',
              type === t
                ? t === 'praise'
                  ? 'bg-amber-500 text-white'
                  : 'bg-flock-navy-600 text-white'
                : 'border border-flock-gray-200 text-flock-navy-600'
            )}
          >
            {t === 'prayer' ? 'Prayer Request' : 'Praise Report'}
          </button>
        ))}
      </div>
      <textarea
        autoFocus
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={3}
        maxLength={1000}
        placeholder={
          type === 'praise' ? 'Share what God has done…' : 'What can we pray for you about?'
        }
        className="w-full rounded-lg border border-flock-gray-200 bg-flock-navy-50 px-3 py-2 text-sm outline-none focus:border-flock-navy-400"
      />
      <div
        className={clsx(
          'text-right text-xs',
          text.length >= 1000 ? 'text-red-600' : 'text-flock-navy-400'
        )}
      >
        {text.length} / 1000
      </div>
      <div className="mt-2 flex items-center justify-between">
        <select
          value={privacy}
          onChange={(e) => setPrivacy(e.target.value as PrivacyLevel)}
          className="rounded-lg border border-flock-gray-200 bg-white px-2 py-1.5 text-xs text-flock-navy-700"
        >
          <option value="public">Visible to my group</option>
          <option value="leaders_only">Leaders only</option>
          <option value="private">Just me</option>
        </select>
        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="rounded-lg border border-flock-gray-200 px-3 py-1.5 text-xs text-flock-navy-600"
          >
            Cancel
          </button>
          <button
            disabled={busy || !text.trim()}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await onSubmit({ prayerText: text.trim(), type, privacyLevel: privacy });
                setText('');
                onClose();
              } catch (e: any) {
                setError(
                  e?.response?.data?.errors?.[0]?.msg ||
                    e?.response?.data?.message ||
                    'Could not submit. Please try again.'
                );
              } finally {
                setBusy(false);
              }
            }}
            className="rounded-lg bg-flock-navy-600 px-4 py-1.5 text-xs font-medium text-white hover:bg-flock-navy-700 disabled:opacity-50"
          >
            {busy ? 'Sharing…' : 'Share'}
          </button>
        </div>
      </div>
    </div>
  );
}

const LEADER_ROLES = ['org_admin', 'campus_pastor', 'ministry_director', 'group_leader'];

interface PrayerWallPageProps {
  /** Group to show. Falls back to the app-level current group. */
  group?: { id: string; name: string } | null;
}

export default function PrayerWallPage({ group }: PrayerWallPageProps) {
  const { currentGroup, user, organizations, isSwitchingOrg } = useAuth();
  const { byGroup, markGroupPrayersSeen } = usePrayerUnreadContext();
  const activeGroup = group ?? currentGroup;
  const groupId = activeGroup?.id ?? null;
  const {
    prayers,
    loading,
    error,
    hasMore,
    loadMore,
    filters,
    setFilters,
    submitPrayer,
    markAsPrayed,
    addEncouragement,
    markAsAnswered,
    deletePrayer,
    editPrayer,
    liftPrayer,
    unliftPrayer,
  } = usePrayers(groupId);

  const { toggleEncouragementReaction } = usePrayerActions();

  const [creating, setCreating] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [highlightId, setHighlightId] = useState<string | null>(null);

  // Leader status resolved from the org that owns THIS group (the global
  // `role` tracks the current org, which can lag during org switches).
  const isLeader = useMemo(() => {
    if (!groupId) return false;
    const membership = organizations.find((o) => o.groups?.some((g) => g.id === groupId));
    return Boolean(membership?.roles.some((r) => LEADER_ROLES.includes(r.role)));
  }, [organizations, groupId]);

  // Mark prayer activity seen while this wall is open: on mount, and again
  // whenever new activity arrives while the tab is visible (keeps the badge
  // pinned at 0 while you're watching — chat semantics).
  const unreadHere = groupId ? (byGroup[groupId] ?? 0) : 0;
  useEffect(() => {
    if (!groupId || isSwitchingOrg) return;
    if (document.visibilityState !== 'visible') return;
    if (unreadHere > 0) markGroupPrayersSeen(groupId);
  }, [groupId, unreadHere, isSwitchingOrg, markGroupPrayersSeen]);
  useEffect(() => {
    if (groupId && !isSwitchingOrg) markGroupPrayersSeen(groupId);
    // Mount/group-change only — markGroupPrayersSeen is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  // Scroll to + briefly highlight a prayer card by its wall id (used by both
  // quick-search and AI-search result rows). Graceful no-op if not rendered.
  const scrollToCard = (wallId: string) => {
    const el = document.getElementById(`prayer-${wallId}`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightId(wallId);
    window.setTimeout(() => setHighlightId((cur) => (cur === wallId ? null : cur)), 2000);
  };

  if (!groupId) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-flock-navy-400">
        Select a group to see its prayer wall.
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <header className="border-b border-flock-gray-200 bg-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-flock-navy-900">Prayer Wall</h1>
            <p className="text-xs text-flock-navy-400">{activeGroup?.name}</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <button
                onClick={() => setSearchOpen(true)}
                title="Search prayers"
                aria-label="Search prayers"
                className="min-h-touch flex items-center justify-center rounded-lg border border-flock-gray-200 p-2 text-flock-navy-500 transition hover:bg-flock-navy-50 hover:text-flock-navy-700"
              >
                <MagnifyingGlassIcon className="h-5 w-5" />
              </button>
              {searchOpen && (
                <PrayerSearchPopover
                  groupId={groupId}
                  prayers={prayers}
                  onScrollToCard={scrollToCard}
                  onClose={() => setSearchOpen(false)}
                />
              )}
            </div>
            <button
              onClick={() => setCreating((v) => !v)}
              className="min-h-touch flex items-center gap-1.5 rounded-lg bg-flock-navy-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-flock-navy-700"
            >
              <PlusIcon className="h-4 w-4" /> Share a Prayer
            </button>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <div className="flex gap-1 rounded-full bg-flock-navy-50 p-1">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.value}
                onClick={() => setFilters({ ...filters, status: tab.value })}
                className={clsx(
                  'rounded-full px-3 py-1 text-xs font-medium transition',
                  filters.status === tab.value
                    ? 'bg-flock-navy-600 text-white'
                    : 'text-flock-navy-500 hover:text-flock-navy-700'
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="flex gap-1 rounded-full bg-flock-navy-50 p-1">
            {TYPE_TABS.map((tab) => (
              <button
                key={tab.value}
                onClick={() => setFilters({ ...filters, type: tab.value })}
                className={clsx(
                  'rounded-full px-3 py-1 text-xs font-medium transition',
                  (filters.type ?? 'all') === tab.value
                    ? 'bg-flock-navy-600 text-white'
                    : 'text-flock-navy-500 hover:text-flock-navy-700'
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto px-6 py-4">
        <div className="mx-auto max-w-2xl space-y-3">
          {creating && (
            <CreatePrayerForm
              onSubmit={(data) => submitPrayer({ ...data, groupId })}
              onClose={() => setCreating(false)}
            />
          )}

          {loading && prayers.length === 0 && (
            <div className="py-12 text-center text-sm text-flock-navy-400">Loading prayers…</div>
          )}
          {error && <div className="py-4 text-center text-sm text-red-600">{error}</div>}
          {!loading && prayers.length === 0 && !creating && (
            <div className="py-12 text-center">
              <p className="text-sm font-medium text-flock-navy-600">No prayers here yet</p>
              <p className="mt-1 text-xs text-flock-navy-400">
                Be the first to share a prayer request or praise report.
              </p>
            </div>
          )}

          {prayers.map((prayer: Prayer) => (
            <div
              key={prayer.id}
              id={`prayer-${prayer.id}`}
              className={clsx(
                'scroll-mt-4 rounded-2xl transition',
                highlightId === prayer.id && 'ring-2 ring-flock-navy-400 ring-offset-2'
              )}
            >
              <PrayerCard
                prayer={prayer}
                currentUserId={user?.id}
                isLeader={isLeader}
                onPray={(p) => markAsPrayed(p.id).catch(() => {})}
                onEncourage={(p, note) => addEncouragement(p.id, note)}
                onReactionToggle={(p, noteId, emoji, reacted) =>
                  toggleEncouragementReaction(p, noteId, emoji, reacted).catch(() => {})
                }
                onMarkAnswered={(p, answerText) => markAsAnswered(p.id, answerText || undefined)}
                onLiftToggle={(p) =>
                  (p.liftedAt && new Date().getTime() - new Date(p.liftedAt as string).getTime() < 48 * 3600_000
                    ? unliftPrayer(p.id)
                    : liftPrayer(p.id)
                  ).catch(() => {})
                }
                onEdit={(p, newText) => editPrayer(p.id, newText)}
                onDelete={(p) => {
                  if (window.confirm('Delete this prayer?')) deletePrayer(p.id).catch(() => {});
                }}
              />
            </div>
          ))}

          {hasMore && (
            <button
              onClick={() => loadMore()}
              className="w-full rounded-lg border border-flock-gray-200 py-2 text-sm text-flock-navy-600 hover:bg-white"
            >
              Load more
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
